import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import {
  AlbumNodeProps,
  currencyAmonuts,
  FolderBreadcrumbText,
  LoadingSpinner,
  SpaceNodeProps,
  VideoPlayer,
} from '../../components/Molecules';
import {
  ClubAccessRestrictionPopup,
  ClubSelectFolderPopup,
} from '../../components/Popups';
import { BreadDonationPopup } from '../../components/Popups/BreadDonationPopup';
import { DonateBreadPopup } from '../../components/Popups/DonateBreadPopup';
import { SwitchMemoryBoostPopup } from '../../components/Popups/SwitchMemoryBoostPopup';
import { AppMainTemplate } from '../../components/Templates';
import { DonateBreadTemplate } from '../../components/Templates/Auth/DonateBreadTemplate';
import { SLICE_PER_BREAD, StaminaType } from '../../constants/currencies';
import { SpacePrivacyPolicy } from '../../constants/space';
import { AppToastErrorOption, AppToastInfoOption } from '../../constants/toast';
import {
  getBibleBoostVideo,
  getMemoryBoostVideo,
} from '../../constants/videos';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useGameCurrency from '../../hooks/useGameCurrency';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { TranslationLocale } from '../../i18n';
import { BalanceType } from '../../models/Base';
import { SpaceListType } from '../../models/SpaceStore';
import { WebBoosterType } from '../../models/UserStore';
import { UpdateCurrentUserResult } from '../../models/UserStore/UserStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';

enum ShowPopupState {
  StartAiRevision = 'startAiRevision',
  DonateBread = 'donateBread',
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}
interface State {
  selectedBoosterType: WebBoosterType | undefined;
  partitionId: string;
  // selectedPeriod: PracticePeriodType;
  // from: Date | undefined;
  // to: Date | undefined;
  // performanceData: SpacePerformanceReportSummary | undefined;
  isFinishedFetchSpace: boolean;
  loadingTotal: number;
  showPopup: ShowPopupState | undefined;
  deferredPrompt: BeforeInstallPromptEvent | null;
  isPromptVisible: boolean;
  donationImageSet: number;
}

const { updateCurrentUser, createDonation } = useUserStore.getState();

const { fetchSpace, fetchHotSpaces, seeSpace } = useSpaceStore.getState();

const AppMainScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showPopup, hidePopup } = usePopup();

  const userId = useSessionStore((store) => store.activeUserId);
  const partitionId = useSessionStore((store) => store.partitionId);

  const userProfile = useUserStore((state) => state.getProfile(userId ?? ''));
  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));

  const currentDate = useMemo(() => new Date(), []);
  // const todayPeriod = useMemo(() => {
  //   return getPeriod(currentDate, PeriodType.Today, i18n.language);
  // }, [currentDate, i18n.language]);

  const [state, setState] = useState<State>({
    selectedBoosterType: userAccount?.settings.webBoosterType,
    partitionId: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.All}`,
    // selectedPeriod: PracticePeriodType.Overall,
    // from: undefined,
    // to: undefined,
    // performanceData: undefined,
    isFinishedFetchSpace: false,
    loadingTotal: 0,
    showPopup: undefined,
    deferredPrompt: null,
    isPromptVisible: false,
    donationImageSet:
      currentDate.getDay() + (userProfile?.statistics.donatedBreadCount ?? 0),
  });

  useEffect(() => {
    setState((s) => ({
      ...s,
      partitionId:
        partitionId ??
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`,
    }));
  }, [partitionId, state.selectedBoosterType]);

  useRedirectEntry();

  const allowance = useUserStore((store) => store.getAllowance());

  const { gameStamina, creationStamina, token, breadSlice, fetchCurrency } =
    useGameCurrency(userAccount, userProfile, allowance);

  const currencies = useMemo(
    () =>
      currencyAmonuts({ gameStamina, creationStamina, token }, [
        StaminaType.GameStamina,
        StaminaType.CreationStamina,
        BalanceType.Token,
      ]),
    [creationStamina, gameStamina, token],
  );

  // MARK: Get Home Classroom
  const classroomId = userId;
  useEffect(() => {
    if (classroomId) {
      fetchSpace(SpaceListType.Home, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId]);

  const classroom = useSpaceStore(
    (store) => store.getSpace(classroomId ?? ''),
    shallow,
  );

  useRedirectHome(state.isFinishedFetchSpace && !classroom);

  useEffect(() => {
    if (classroomId) {
      seeSpace(classroomId).catch(console.error);
    }
  }, [classroomId]);

  const breadcrumbs = useMemo(() => {
    const folders =
      classroom?.getAllPartitions(userAccount?.settings.webBoosterType) ?? [];
    const selectFolder = folders.find(
      (folder) => folder.partitionId === state.partitionId,
    );
    if (selectFolder) {
      return [selectFolder];
    }
    return [];
  }, [userAccount?.settings.webBoosterType, classroom, state.partitionId]);

  // MARK: Get Recent Album Classroom
  const albumNormalFieldName = `${userId}_${state.selectedBoosterType}_${state.partitionId}`;
  const albumNormalField = useSpaceStoreListField(
    'albums',
    albumNormalFieldName,
  );

  const fetchAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchAlbums(
        {
          type: albumNormalFieldName,
          classroomId: classroomId ?? '',
          webBoosterType: state.selectedBoosterType,
          partitionId:
            state.partitionId ===
            `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
              ? undefined
              : state.partitionId,
          withAlbumSummaries: true,
        },
        cursor,
      ),
    [
      albumNormalFieldName,
      classroomId,
      state.selectedBoosterType,
      state.partitionId,
    ],
  );

  const { refresh: refreshAlbums } = useFetchList(
    albumNormalField,
    fetchAlbums,
  );

  useEffect(() => {
    if (classroomId) {
      refreshAlbums().catch(console.error);
    }
  }, [refreshAlbums, classroomId]);

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(albumNormalFieldName);
  }, arrayEqual(shallow));

  const albumSummaries = useRecordStore((store) => {
    const gameSetIds = albums.map((item) => item.gameSetId);

    return gameSetIds.map((gameSetId) =>
      store.getAlbumSummary(gameSetId, userId ?? ''),
    );
  }, arrayEqual(shallow));

  const onClickAlbum = useCallback(
    (_classroomId: string, gameSetId: string) => {
      navigate(`/club/${_classroomId}/journey/${gameSetId}`, {
        state: { withBackState: true },
      });
    },
    [navigate],
  );

  const albumData = useMemo<AlbumNodeProps[]>(() => {
    return albums.map((album) => {
      const albumSummary = albumSummaries.find(
        (item) => item?.gameSetId === album.gameSetId,
      );
      return {
        album: album,
        albumSummary: albumSummary,
        onClick: () => {
          onClickAlbum(album.classroomId, album.gameSetId);
        },
        isHideName: true,
      };
    });
  }, [albums, albumSummaries, onClickAlbum]);

  const hasRewardBox = useMemo(() => {
    const isAllPartitionId =
      state.partitionId ===
      `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`;

    return isAllPartitionId && albums.length === 0;
  }, [albums.length, state.partitionId, state.selectedBoosterType]);

  // MARK: Get HOT Classroom
  useEffect(() => {
    fetchHotSpaces({
      label:
        state.selectedBoosterType === WebBoosterType.Normal
          ? 'trending'
          : 'learning',
      state: 'available',
    }).catch(console.error);
  }, [state.selectedBoosterType]);

  // MARK: Get Recent Classroom
  const recentSpaceField = useSpaceStoreListField(
    'spaces',
    SpaceListType.Recent,
  );

  const fetchRecentSpaces = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore
        .getState()
        .fetchRecentSpaces(state.selectedBoosterType, cursor),
    [state.selectedBoosterType],
  );

  const { fetchAll: fetchAllRecentSpaces } = useFetchList(
    recentSpaceField,
    fetchRecentSpaces,
  );
  useEffect(() => {
    fetchAllRecentSpaces().catch(console.error);
  }, [fetchAllRecentSpaces]);

  const onClubAccessRestricted = useCallback(() => {
    showPopup(
      <ClubAccessRestrictionPopup
        onDetails={() => {
          hidePopup();
          navigate('/store');
          window.scrollTo(0, 0);
        }}
        onCancel={hidePopup}
      />,
    );
  }, [showPopup, hidePopup, navigate]);

  const onClickClub = useCallback(
    (selectedClassroomId: string) => {
      const selectedClassroom = useSpaceStore
        .getState()
        .getSpace(selectedClassroomId ?? '');
      const selectedClassroomMembership = useSpaceStore
        .getState()
        .getActiveUserMembership(selectedClassroomId ?? '');
      if (
        selectedClassroom?.privacyPolicy === SpacePrivacyPolicy.Public ||
        (selectedClassroom?.privacyPolicy ===
          SpacePrivacyPolicy.PrivateAndVisible &&
          selectedClassroomMembership?.isInGroup)
      ) {
        navigate(`/club/${selectedClassroomId}`);
      } else {
        onClubAccessRestricted();
      }
    },
    [navigate, onClubAccessRestricted],
  );

  const hotsSpaces = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Hot);
  }, arrayEqual(shallow));

  const recentSpaces = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Recent);
  }, arrayEqual(shallow));

  const recentSpacesData = useMemo<SpaceNodeProps[]>(() => {
    return recentSpaces
      .filter((item) => item.classroomId !== userId)
      .map((space) => {
        return {
          space: space,
          webBoosterType: state.selectedBoosterType,
          onClickClub: () => {
            onClickClub(space.classroomId);
          },
          // isHideName: true,
        };
      });
  }, [onClickClub, recentSpaces, state.selectedBoosterType, userId]);

  // MARK: Fetch Performance Report
  // const fetchSpacePerformanceReport = useCallback(() => {
  //   getPerformanceReport({
  //     classroomId: classroomId ?? '',
  //     webBoosterType: state.selectedBoosterType,
  //     partitionId:
  //       state.partitionId ===
  //       `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
  //         ? undefined
  //         : state.partitionId,
  //     from: todayPeriod?.from?.getTime().toString(),
  //     to: todayPeriod?.to?.getTime().toString(),
  //   })
  //     .then((summary) => {
  //       if (summary) {
  //         setState((s) => ({
  //           ...s,
  //           performanceData: summary.performanceReport,
  //         }));
  //       }
  //     })
  //     .catch(console.error);
  // }, [
  //   classroomId,
  //   state.partitionId,
  //   state.selectedBoosterType,
  //   todayPeriod?.from,
  //   todayPeriod?.to,
  // ]);

  // useEffect(() => {
  //   if (classroomId) {
  //     fetchSpacePerformanceReport();
  //   }
  // }, [classroomId, fetchSpacePerformanceReport]);

  // const { avgGameScore, completedGames } = useMemo(() => {
  //   if (state.performanceData) {
  //     const completedGameCount = state.performanceData.completedGameCount;
  //     const scoreTotal = state.performanceData.scoreTotal;
  //     return {
  //       avgGameScore: parseFloat(
  //         numbro(
  //           completedGameCount > 0 ? scoreTotal / completedGameCount : 0,
  //         ).format({
  //           mantissa: 2,
  //           trimMantissa: true,
  //           optionalMantissa: true,
  //         }),
  //       ),
  //       completedGames: completedGameCount,
  //     };
  //   }
  //   return {
  //     avgGameScore: 0,
  //     completedGames: 0,
  //   };
  // }, [state.performanceData]);

  const onClickShare = useCallback(() => {
    const pageUrl = 'https://joyajoy.com';

    const updatedUrl = new URL(pageUrl);
    const queryParams = new URLSearchParams();
    updatedUrl.search = queryParams.toString();

    if (navigator.share) {
      navigator
        .share({ url: decodeURIComponent(updatedUrl.toString()) })
        .catch((err) => {
          console.error('Failed to share the URL: ', err);
        });
    } else {
      navigator.clipboard
        .writeText(decodeURIComponent(updatedUrl.toString()))
        .then(() => {
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [t]);

  // MARK: Other Functions
  const onClickTrialRegister = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  const onClickHomeClub = useCallback(() => {
    navigate(`/club/journey`);
  }, [navigate]);

  // const onClickPerformanceReport = useCallback(() => {
  //   navigate(`/club/${userId}/performance-report`);
  // }, [navigate, userId]);

  const onClickHotClubs = useCallback(() => {
    navigate('/club/hot');
  }, [navigate]);

  const onClickRevisionDetail = useCallback(() => {
    navigate(`/club/${classroomId}/memory-boost`);
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = 'auto'; // instant scroll
      document.body.style.scrollBehavior = 'auto';
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
  }, [navigate, classroomId]);

  const onClickCurrency = useCallback(() => {
    navigate(`/wallet`);
    window.scrollTo(0, 0);
  }, [navigate]);

  const onClickCreateGames = useCallback(() => {
    useDraftStore.getState().clear();
    navigate('/create/memory-game/select-type');
  }, [navigate]);

  const onClickBoostVideo = useCallback(() => {
    const getVideo =
      state.selectedBoosterType === WebBoosterType.Normal
        ? getMemoryBoostVideo
        : getBibleBoostVideo;

    showPopup(
      <VideoPlayer
        url={getVideo(getUiLocale(i18n.language as TranslationLocale))}
        playing={true}
        muted={false}
        // playIcon={<></>}
      />,
      {
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: '16/9',
        withCross: true,
      },
    );
  }, [i18n.language, showPopup, state.selectedBoosterType]);

  const onSelectBooster = useCallback(
    (webBoosterType: WebBoosterType) => {
      const handleConfirm = async () => {
        if (userAccount) {
          const res = await updateCurrentUser({
            user: {
              settings: {
                ...userAccount.settings,
                webBoosterType: webBoosterType,
              },
            },
          });

          if (res === UpdateCurrentUserResult.Success) {
            setState((s) => ({ ...s, selectedBoosterType: webBoosterType }));
            useSessionStore.getState().setPartitionId(null);
          } else {
            // to-do error message
          }
        } else {
          // to-do error message
        }
      };
      hidePopup();
      handleConfirm().catch(console.error);
    },
    [userAccount, hidePopup],
  );

  const onClickSwitchBooster = useCallback(() => {
    showPopup(
      <SwitchMemoryBoostPopup
        selectedBooster={state.selectedBoosterType}
        onPrimary={hidePopup}
        onSelect={onSelectBooster}
      />,
    );
  }, [state.selectedBoosterType, hidePopup, onSelectBooster, showPopup]);

  const onShareLink = useCallback(() => {
    // to-do edit the link so that user onclick => enter normal or bible folder mode, I'm not too familar with the requirement
    const pageUrl = window.location.href;

    const updatedUrl = new URL(pageUrl);
    updatedUrl.pathname = '/start';
    const queryParams = new URLSearchParams();
    queryParams.set('booster', state.selectedBoosterType ?? '');
    updatedUrl.search = queryParams.toString();

    if (navigator.share) {
      navigator
        .share({ url: decodeURIComponent(updatedUrl.toString()) })
        .catch((err) => {
          console.error('Failed to share the URL: ', err);
        });
    } else {
      navigator.clipboard
        .writeText(decodeURIComponent(updatedUrl.toString()))
        .then(() => {
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [state.selectedBoosterType, t]);

  const onClickAccountSetting = useCallback(() => {
    navigate(`/settings/account`);
  }, [navigate]);

  const onClickBreadcrumb = useCallback(
    (folder: string) => {
      if (classroom) {
        showPopup(
          <ClubSelectFolderPopup
            webBoosterType={userAccount?.settings.webBoosterType}
            space={classroom}
            selectedFolderId={folder}
            folders={classroom.getAllPartitions(
              userAccount?.settings.webBoosterType,
            )}
            newFolder={undefined}
            onClickSelect={onClickBreadcrumb}
            onCancel={hidePopup}
            onConfirm={() => {
              useSessionStore.getState().setPartitionId(folder);
              hidePopup();
            }}
          />,
        );
      }
    },
    [classroom, showPopup, userAccount?.settings.webBoosterType, hidePopup],
  );

  const onClickScrollBread = useCallback(() => {
    const element = document.querySelector('.joy-to-the-worldArea');
    if (element) {
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - 57,
        behavior: 'smooth',
      });
    }
  }, []);

  const { hash } = useLocation();
  useEffect(() => {
    if (hash === '#donate-bread') {
      onClickScrollBread();
    }
  }, [hash, onClickScrollBread]);

  const onClickRecentSpaces = useCallback(() => {
    navigate('/club/recent');
    window.scrollTo(0, 0);
  }, [navigate]);

  // const onClickPracticeDetail = useCallback(() => {
  //   showPopup(
  //     <AboutDailyPracticePopup
  //       webBoosterType={state.selectedBoosterType}
  //       onCancel={hidePopup}
  //     />,
  //   );
  // }, [hidePopup, showPopup, state.selectedBoosterType]);

  // const onClickPeriod = useCallback(() => {
  //   showPopup(
  //     <SelectPracticePeriodPopup
  //       selectPeriod={state.selectedPeriod}
  //       onPrimary={hidePopup}
  //       onPeriod={(selected, from, to) => {
  //         setState((s) => ({
  //           ...s,
  //           selectedPeriod: selected,
  //           from: from,
  //           to: to,
  //         }));
  //         hidePopup();
  //       }}
  //     />,
  //   );
  // }, [hidePopup, showPopup, state.selectedPeriod]);

  // const onClickBoostGame = useCallback(async () => {
  //   // if (!isWebPlanUnlocked) {
  //   //   onClubAccessRestricted();
  //   // } else {
  //   if (classroomId) {
  //     setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
  //     const revisionGame = await useRecordStore
  //       .getState()
  //       .getRevisionScheduleGame({
  //         classroomId,
  //         webBoosterType: state.selectedBoosterType,
  //         partitionId:
  //           state.partitionId ===
  //           `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
  //             ? undefined
  //             : state.partitionId,
  //         from: state.from?.getTime().toString(),
  //         to: state.to?.getTime().toString(),
  //       });
  //     setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
  //     if (revisionGame) {
  //       navigate(
  //         `/club/${revisionGame.classroomId}/journey/${revisionGame.gameSetId}/game/${revisionGame.gameId}`,
  //       );
  //       hidePopup();
  //     } else {
  //       toast(
  //         t(
  //           'No practice available yet. Auto-Scheduled Daily Practice prioritizes your practice according to your previous score and completion time.',
  //         ),
  //         AppToastInfoOption,
  //       );
  //     }
  //   }
  //   // }
  // }, [
  //   classroomId,
  //   hidePopup,
  //   navigate,
  //   state.from,
  //   state.partitionId,
  //   state.selectedBoosterType,
  //   state.to,
  //   t,
  // ]);

  // const onPressCancel = useCallback(() => {
  //   setState((s) => ({ ...s, showPopup: undefined }));
  // }, []);

  // const onClickStartAIRevision = useCallback(() => {
  //   setState((s) => ({ ...s, showPopup: ShowPopupState.StartAiRevision }));
  // }, []);

  const onClickBreadDonated = useCallback(() => {
    showPopup(<BreadDonationPopup onPrimary={hidePopup} />);
  }, [hidePopup, showPopup]);

  const onClickGetMoreBread = useCallback(() => {
    navigate(`/get-more-bread`);
    window.scrollTo(0, 0);
  }, [navigate]);

  const onClickDonateBread = useCallback(() => {
    const bread = Math.floor(breadSlice / SLICE_PER_BREAD);
    if (bread > 0) {
      showPopup(
        <DonateBreadPopup
          bread={bread}
          onPrimary={() => {
            hidePopup();
            setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));

            createDonation({ bread })
              .then((isSuccess) => {
                if (isSuccess) {
                  setState((s) => ({
                    ...s,
                    showPopup: ShowPopupState.DonateBread,
                  }));
                } else {
                  toast(
                    t(
                      'Failed to donate bread. Please refresh the page and try again.',
                    ),
                    AppToastErrorOption,
                  );
                }
              })
              .finally(() =>
                setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 })),
              )
              .catch(console.error);
          }}
          onCancel={hidePopup}
        />,
      );
    }
  }, [breadSlice, hidePopup, showPopup, t]);

  // MARKS: the add home Screen button
  const isAddHomeButtonOnTop = useMemo(() => {
    const hasClickAddToHomeScreen =
      localStorage.getItem('hasClickAddToHomeScreen') === 'true';
    return !hasClickAddToHomeScreen && !state.isPromptVisible;
  }, [state.isPromptVisible]);

  const onClickAddToHomeScreen = useCallback(() => {
    navigate('/add-to-home-guide');
    window.scrollTo(0, 0);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setState((s) => ({
        ...s,
        deferredPrompt: e as BeforeInstallPromptEvent,
        isPromptVisible: true,
      }));
    };

    if ('BeforeInstallPromptEvent' in window) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    } else {
      setState((s) => ({ ...s, isPromptVisible: true }));
    }

    return () => {
      if ('BeforeInstallPromptEvent' in window) {
        window.removeEventListener(
          'beforeinstallprompt',
          handleBeforeInstallPrompt,
        );
      }
    };
  }, []);

  const handleAddToHomeScreen = async () => {
    localStorage.setItem('hasClickAddToHomeScreen', 'true');
    if (state.deferredPrompt) {
      try {
        await state.deferredPrompt.prompt();
        const choiceResult = await state.deferredPrompt.userChoice;

        if (choiceResult.outcome === 'accepted') {
          // console.log('User accepted the A2HS prompt');
        } else {
          // console.log('User dismissed the A2HS prompt');
          onClickAddToHomeScreen();
        }
        setState((s) => ({
          ...s,
          deferredPrompt: null,
          isPromptVisible: false,
        }));
      } catch (error) {
        // console.error('Error handling the A2HS prompt:', error);
        onClickAddToHomeScreen();
      }
    } else {
      onClickAddToHomeScreen();
    }
  };

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      <AppMainTemplate
        profile={userProfile}
        breadSlice={breadSlice}
        currencies={currencies}
        webBoosterType={state.selectedBoosterType}
        breadcrumbs={breadcrumbs}
        hasRewardBox={hasRewardBox}
        albumData={albumData}
        recentSpacesData={recentSpacesData}
        hotSpaces={hotsSpaces}
        donationImageSet={state.donationImageSet}
        isAddHomeButtonOnTop={isAddHomeButtonOnTop}
        onClickTrialRegister={onClickTrialRegister}
        onClickHomeClub={onClickHomeClub}
        onClickCurrency={onClickCurrency}
        onClickCreateGames={onClickCreateGames}
        onSeeAllHotClubs={onClickHotClubs}
        onClickClub={onClickClub}
        onClickBoostVideo={onClickBoostVideo}
        onClickSwitchBooster={onClickSwitchBooster}
        onClickBreadcrumb={() => {
          onClickBreadcrumb(state.partitionId);
        }}
        onClickRevisionDetail={onClickRevisionDetail}
        onShareLink={onShareLink}
        onClickRecentSpaces={onClickRecentSpaces}
        onClickAccountSetting={onClickAccountSetting}
        onClickScrollBread={onClickScrollBread}
        onClickBreadDonated={onClickBreadDonated}
        onClickGetMoreBread={onClickGetMoreBread}
        onClickDonateBread={onClickDonateBread}
        onClickShare={onClickShare}
        onClickAddToHomeScreen={() => {
          handleAddToHomeScreen().catch(console.error);
        }}
      />
      {/* {state.showPopup === ShowPopupState.StartAiRevision && classroom && (
        <AIScheduledDailyPracticeSelectionPopup
          space={classroom}
          webBoosterType={state.selectedBoosterType}
          breadcrumbs={breadcrumbs}
          selectedPeriod={
            getPracticePeriod(currentDate, state.selectedPeriod)?.name ?? ''
          }
          onSelectBooster={onClickSwitchBooster}
          onSelectFolder={() => {
            onClickBreadcrumb(state.partitionId);
          }}
          onSelectPeriod={onClickPeriod}
          onConfirm={() => {
            onClickBoostGame().catch(console.error);
          }}
          onCancel={onPressCancel}
        />
      )} */}
      {state.showPopup === ShowPopupState.DonateBread && (
        <DonateBreadTemplate
          donationImageSet={state.donationImageSet}
          onClose={() => {
            setState((s) => ({ ...s, showPopup: undefined }));
            fetchCurrency()
              .then(() => {
                setState((s) => ({
                  ...s,
                  donationImageSet:
                    currentDate.getDay() +
                    (useUserStore.getState().getProfile(userId ?? '')
                      ?.statistics.donatedBreadCount ?? 0),
                  // useUserStore directly to get latest profile
                }));
              })
              .catch(console.error);
          }}
        />
      )}
    </>
  );
};

export default AppMainScreen;
